/**
 * Reusable app-shell-Header.
 *
 * Brand-Text klickbar → /dashboard
 * Avatar (Initialen oder Profilbild) klickbar → /profile
 * OnlineBadge falls auf capture
 * Optionaler Back-Button links vom Brand (Sprint 9 · S9.1)
 */
import { Link, useNavigate } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { OnlineBadge } from './OnlineBadge';

interface Props {
  /** Title-Suffix nach 'LEADCRAFT', z.B. 'ERFASSUNG' */
  subtitle?: string;
  /** Online/Offline-Badge zeigen (für Capture) */
  showOnlineBadge?: boolean;
  /** Optionaler Slot rechts vor Avatar (z.B. Action-Buttons) */
  rightSlot?: ReactNode;
  /**
   * Ziel-Route für den ZURÜCK-Button. Default: /dashboard.
   * `null` blendet den Button aus (z.B. auf Dashboard selbst).
   */
  backTo?: string | null;
}

export function AppHeader({ subtitle, showOnlineBadge, rightSlot, backTo = '/dashboard' }: Props) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Sprint 21 (S21.1) · Image-Load-Error → Initialen-Fallback (statt
  // Browser-Default-Broken-Image-Icon = "Fragezeichen").
  const [imgError, setImgError] = useState(false);
  if (!user) return null;

  const initials = user.full_name
    .split(' ')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  function handleBack() {
    if (!backTo) return;
    // Wenn Browser-History tiefer ist als Frischlade, native back nutzen,
    // sonst auf backTo navigieren.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(backTo);
    }
  }

  return (
    <header className="app-shell__header">
      <div className="app-shell__brand">
        {backTo ? (
          <button
            type="button"
            className="app-shell__back"
            onClick={handleBack}
            aria-label="Zurück"
          >
            <ArrowLeft size={16} strokeWidth={2.5} />
            <span className="app-shell__back-label">ZURÜCK</span>
          </button>
        ) : null}
        <Link to="/dashboard" className="app-shell__brand-link" aria-label="Zum Dashboard">
          {user.branding.logo_url ? (
            <img
              src={user.branding.logo_url}
              alt={user.branding.tenant_name ?? 'Tenant Logo'}
              className="app-shell__brand-logo"
            />
          ) : (
            'LEADCRAFT'
          )}
        </Link>
        {subtitle ? <span className="app-shell__brand-sep"> · {subtitle}</span> : null}
        {showOnlineBadge ? <OnlineBadge /> : null}
      </div>
      <div className="app-shell__user">
        {rightSlot}
        <Link to="/profile" className="app-shell__avatar" aria-label="Mein Profil">
          {user.profile_image_url && !imgError ? (
            <img
              src={user.profile_image_url}
              alt=""
              onError={() => setImgError(true)}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="app-shell__avatar-initials">{initials || '?'}</span>
          )}
        </Link>
        <span className="app-shell__user-name">{user.full_name}</span>
        <button onClick={() => void logout()} className="app-shell__logout">
          ABMELDEN
        </button>
      </div>
    </header>
  );
}
